import { useEffect, useRef } from 'react';
import { UPLOAD_SCENE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { ImagePrompt, ReferencePrompt } from '@/app/types';
import { extractTempImageData, uploadFile, uploadTempImages } from '@/app/utils/client';

export const useReferenceTempImagesUpload = (
  referencePrompts: ReferencePrompt[],
  updateReferenceAndMentions: (id: string, updates: Partial<ReferencePrompt>) => void,
) => {
  const userInfo = useStore((state) => state.userInfo);
  const processedUploadKeysRef = useRef<Set<string>>(new Set());
  const pendingUploadKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!userInfo) {
      return;
    }

    const pendingUploads = referencePrompts
      .filter(
        (ref): ref is ReferencePrompt & { type: 'image'; prompt: ImagePrompt; status: 'done' } =>
          ref.type === 'image' && ref.prompt !== null && ref.status === 'done',
      )
      .map((ref) => {
        const tempData = extractTempImageData(ref.prompt);
        const remoteSelectedUploadKey =
          !tempData && ref.prompt._tempSelectedImage && !ref.prompt.selectedImage ? `selected:${ref.id}` : null;

        if (!tempData && !remoteSelectedUploadKey) {
          return null;
        }

        const uploadKey = tempData ? `source:${tempData.id}` : remoteSelectedUploadKey;
        if (!uploadKey) {
          return null;
        }

        if (processedUploadKeysRef.current.has(uploadKey) || pendingUploadKeysRef.current.has(uploadKey)) {
          return null;
        }

        return { ref, tempData, uploadKey };
      })
      .filter(
        (
          item,
        ): item is {
          ref: ReferencePrompt & { type: 'image'; prompt: ImagePrompt; status: 'done' };
          tempData: NonNullable<ReturnType<typeof extractTempImageData>> | null;
          uploadKey: string;
        } => item !== null,
      );

    if (pendingUploads.length === 0) {
      return;
    }

    const uploadPromises = pendingUploads.map(async ({ ref, tempData, uploadKey }) => {
      pendingUploadKeysRef.current.add(uploadKey);
      updateReferenceAndMentions(ref.id, { status: 'uploading' });

      try {
        if (tempData) {
          const results = await uploadTempImages([tempData]);
          const result = results.get(tempData.id);

          if (!result || !ref.prompt) {
            console.error('Failed to upload temp image:', tempData.id);
            updateReferenceAndMentions(ref.id, { status: 'done' });
            return;
          }

          const { _tempFile, _tempSelectedImage, ...promptWithoutTemp } = ref.prompt;
          updateReferenceAndMentions(ref.id, {
            status: 'done',
            prompt: {
              ...promptWithoutTemp,
              sourceImage: result.sourceUri,
              sourceImagePreview: ref.prompt.sourceImagePreview,
              ...(result.selectedUri
                ? {
                    selectedImage: result.selectedUri,
                    selectedImagePreview: ref.prompt.selectedImagePreview || ref.prompt.sourceImagePreview,
                  }
                : {
                    selectedImage: result.sourceUri,
                    selectedImagePreview: ref.prompt.sourceImagePreview,
                  }),
            },
          });
          processedUploadKeysRef.current.add(uploadKey);
          return;
        }

        const currentPrompt = ref.prompt;
        if (!currentPrompt?._tempSelectedImage) {
          updateReferenceAndMentions(ref.id, { status: 'done' });
          return;
        }

        const selectedUploadResult = await uploadFile({
          file: currentPrompt._tempSelectedImage,
          width: currentPrompt.selectedImageWidth ?? currentPrompt.sourceImageWidth ?? 0,
          height: currentPrompt.selectedImageHeight ?? currentPrompt.sourceImageHeight ?? 0,
          scene: UPLOAD_SCENE.VIDU,
        });

        if (selectedUploadResult.error || !selectedUploadResult.uri) {
          console.error('Failed to upload temp selected image:', ref.id, selectedUploadResult.error);
          updateReferenceAndMentions(ref.id, { status: 'done' });
          return;
        }

        const { _tempSelectedImage, ...promptWithoutTempSelectedImage } = currentPrompt;
        updateReferenceAndMentions(ref.id, {
          status: 'done',
          prompt: {
            ...promptWithoutTempSelectedImage,
            selectedId: selectedUploadResult.id ?? promptWithoutTempSelectedImage.selectedId,
            selectedImage: selectedUploadResult.uri,
            selectedImagePreview:
              promptWithoutTempSelectedImage.selectedImagePreview || promptWithoutTempSelectedImage.sourceImagePreview,
          },
        });
        processedUploadKeysRef.current.add(uploadKey);
      } finally {
        pendingUploadKeysRef.current.delete(uploadKey);
      }
    });

    Promise.all(uploadPromises).catch((error) => {
      console.error('Failed to upload temp images:', error);
    });
  }, [userInfo, referencePrompts, updateReferenceAndMentions]);
};
